<template>
  <div class="gs-asset-type">
    <asset-segmented
      :items="socialMediaPostsTabs"
      :current="socialMediaPostsTab"
      :error-values="socialMediaPostsErrorTabs"
      label="Social networks"
      @select="handleSocialMediaPostsTabChange"
    />

    <div v-if="socialMediaPostsTab === 'facebook'" class="gs-asset-split">
      <template v-for="(post, index) in facebookPosts">
        <section
          v-if="isCurrentFacebookPostsTab(post)"
          :key="post.id"
          class="gs-asset-editor"
        >
          <header class="gs-asset-editor__head">
            <div class="gs-asset-editor__head-fields">
              <gs-field :label="$t('frontend_resources_name')">
                <text-input
                  v-model="post.name"
                  :validator="$v.localFunnelResource.social_media_posts.facebook.$each[index].name"
                  :attribute="$t('frontend_groovemember_filters_post_name')"
                  :placeholder="$t('frontend_resources_name')"
                />
              </gs-field>
              <gs-field :label="$t('frontend_resources_link_to')">
                <gs-name-select
                  v-model="post.landingPage"
                  size="full"
                  max-height="220px"
                  :options="landingPagesOptions"
                />
              </gs-field>
            </div>
            <div class="gs-asset-editor__controls">
              <span class="gs-asset-editor__toggle">
                <span>Show to affiliates</span>
                <gd-toggle v-model="post.enabled" :value="post.enabled" />
              </span>
              <button
                type="button"
                class="gs-asset-editor__delete"
                data-action="delete-facebook-post"
                :title="`Delete ${facebookName} post`"
                :aria-label="`Delete ${facebookName} post`"
                @click="openConfirmModal(index, 'deleteFacebookPost')"
              >
                <trash-icon class="w-4 h-4 fill-current" />
              </button>
            </div>
          </header>

          <div class="gs-asset-editor__section">
            <field-error
              :validator="$v.localFunnelResource.social_media_posts.facebook.$each[index].post"
              :attribute="$t('frontend_groovesell_post_description')"
            >
              <template #default="{ hasErrors, message }">
                <gs-field :label="$t('frontend_resources_post')" :error="message">
                  <textarea
                    v-model="post.post"
                    class="gs-textarea"
                    :class="{ 'has-error': hasErrors }"
                    :placeholder="$t('frontend_resources_post')"
                  ></textarea>
                </gs-field>
              </template>
            </field-error>
            <div class="gs-asset-editor__foot">
              <span class="gs-asset-editor__count">{{ characterCount(post.post) }}</span>
              <gs-name-select placeholder="Insert" max-height="min(280px, 40vh)" :actions="facebookPostInsertActions(index)" size="action" />
            </div>
          </div>
        </section>
      </template>

      <!-- Only shown when there is something to pick: with an empty list the
           list's own empty state is the single message. -->
      <div v-if="hasFacebookPosts && !selectedFacebookPost" class="gs-asset-empty">
        <p>Select a post from the list to edit it.</p>
      </div>
      <asset-item-list
        :title="`${facebookName} posts`"
        :items="facebookPosts"
        :selected-id="selectedFacebookPostId"
        :add-label="$t('frontend_resources_add_new')"
        name-fallback="Untitled post"
        :empty-copy="$t('frontend_resources_you_donthave_any_facebook_posts')"
        enabled-key="enabled"
        :error-ids="facebookPostsErrorIds"
        @select="selectFacebookPost"
        @add="createFacebookPost()"
        @reorder="reorderFacebookPosts"
      />
    </div>

    <div v-else-if="socialMediaPostsTab === 'twitter'" class="gs-asset-split">
      <template v-for="(post, index) in twitterTweets">
        <section
          v-if="isCurrentTwitterTweetsTab(post)"
          :key="post.id"
          class="gs-asset-editor"
        >
          <header class="gs-asset-editor__head">
            <div class="gs-asset-editor__head-fields">
              <gs-field :label="$t('frontend_resources_name')">
                <text-input
                  v-model="post.name"
                  :validator="$v.localFunnelResource.social_media_posts.twitter.$each[index].name"
                  :attribute="$t('frontend_groovemember_filters_post_name')"
                  :placeholder="$t('frontend_resources_name')"
                />
              </gs-field>
              <gs-field :label="$t('frontend_resources_link_to')">
                <gs-name-select
                  v-model="post.landingPage"
                  size="full"
                  max-height="220px"
                  :options="landingPagesOptions"
                />
              </gs-field>
            </div>
            <div class="gs-asset-editor__controls">
              <span class="gs-asset-editor__toggle">
                <span>Show to affiliates</span>
                <gd-toggle v-model="post.enabled" :value="post.enabled" />
              </span>
              <button
                type="button"
                class="gs-asset-editor__delete"
                data-action="delete-twitter-tweet"
                :title="`Delete ${twitterName} post`"
                :aria-label="`Delete ${twitterName} post`"
                @click="openConfirmModal(index, 'deleteTwitterTweet')"
              >
                <trash-icon class="w-4 h-4 fill-current" />
              </button>
            </div>
          </header>

          <div class="gs-asset-editor__section">
            <field-error
              :validator="$v.localFunnelResource.social_media_posts.twitter.$each[index].tweet"
              :attribute="$t('frontend_resources_tweet')"
            >
              <template #default="{ hasErrors, message }">
                <gs-field
                  :label="$t('frontend_resources_post')"
                  :error="message"
                  :hint="`${twitterName} posts are cut off after 280 characters for most accounts.`"
                >
                  <textarea
                    v-model="post.tweet"
                    class="gs-textarea gs-textarea--short"
                    :class="{ 'has-error': hasErrors }"
                    :placeholder="$t('frontend_resources_post')"
                  ></textarea>
                </gs-field>
              </template>
            </field-error>
            <div class="gs-asset-editor__foot">
              <span
                class="gs-asset-editor__count"
                :class="{ 'is-over': isOverTweetLimit(post.tweet) }"
              >{{ tweetCount(post.tweet) }}</span>
              <gs-name-select placeholder="Insert" max-height="min(280px, 40vh)" :actions="twitterTweetInsertActions(index)" size="action" />
            </div>
          </div>
        </section>
      </template>

      <div v-if="hasTwitterPosts && !selectedTwitterTweet" class="gs-asset-empty">
        <p>Select a post from the list to edit it.</p>
      </div>
      <asset-item-list
        :title="`${twitterName} posts`"
        :items="twitterTweets"
        :selected-id="selectedTwitterTweetId"
        :add-label="$t('frontend_resources_add_new')"
        name-fallback="Untitled post"
        :empty-copy="$t('frontend_resources_you_currently_dont_have_any_tweets')"
        enabled-key="enabled"
        :error-ids="twitterTweetsErrorIds"
        @select="selectTwitterTweet"
        @add="createTwitterTweet()"
        @reorder="reorderTwitterTweets"
      />
    </div>

    <div v-else-if="socialMediaPostsTab === 'linkedin'" class="gs-asset-split">
      <template v-for="(post, index) in linkedinPosts">
        <section
          v-if="isCurrentLinkedinPostsTab(post)"
          :key="post.id"
          class="gs-asset-editor"
        >
          <header class="gs-asset-editor__head">
            <div class="gs-asset-editor__head-fields">
              <gs-field :label="$t('frontend_resources_name')">
                <text-input
                  v-model="post.name"
                  :validator="$v.localFunnelResource.social_media_posts.linkedin.$each[index].name"
                  :attribute="$t('frontend_groovemember_filters_post_name')"
                  :placeholder="$t('frontend_resources_name')"
                />
              </gs-field>
              <gs-field :label="$t('frontend_resources_link_to')">
                <gs-name-select
                  v-model="post.landingPage"
                  size="full"
                  max-height="220px"
                  :options="landingPagesOptions"
                />
              </gs-field>
            </div>
            <div class="gs-asset-editor__controls">
              <span class="gs-asset-editor__toggle">
                <span>Show to affiliates</span>
                <gd-toggle v-model="post.enabled" :value="post.enabled" />
              </span>
              <button
                type="button"
                class="gs-asset-editor__delete"
                data-action="delete-linkedin-post"
                :title="`Delete ${linkedinName} post`"
                :aria-label="`Delete ${linkedinName} post`"
                @click="openConfirmModal(index, 'deleteLinkedinPost')"
              >
                <trash-icon class="w-4 h-4 fill-current" />
              </button>
            </div>
          </header>

          <div class="gs-asset-editor__section">
            <field-error
              :validator="$v.localFunnelResource.social_media_posts.linkedin.$each[index].post"
              attribute="Post"
            >
              <template #default="{ hasErrors, message }">
                <gs-field :label="$t('frontend_resources_post')" :error="message">
                  <textarea
                    v-model="post.post"
                    class="gs-textarea"
                    :class="{ 'has-error': hasErrors }"
                    :placeholder="$t('frontend_resources_post')"
                  ></textarea>
                </gs-field>
              </template>
            </field-error>
            <div class="gs-asset-editor__foot">
              <span class="gs-asset-editor__count">{{ characterCount(post.post) }}</span>
              <gs-name-select placeholder="Insert" max-height="min(280px, 40vh)" :actions="linkedinPostInsertActions(index)" size="action" />
            </div>
          </div>
        </section>
      </template>

      <div v-if="hasLinkedinPosts && !selectedLinkedinPost" class="gs-asset-empty">
        <p>Select a post from the list to edit it.</p>
      </div>
      <asset-item-list
        :title="`${linkedinName} posts`"
        :items="linkedinPosts"
        :selected-id="selectedLinkedinPostId"
        :add-label="$t('frontend_resources_add_new')"
        name-fallback="Untitled post"
        :empty-copy="$t('frontend_resources_you_currently_donthave_linkedin_posts')"
        enabled-key="enabled"
        :error-ids="linkedinPostsErrorIds"
        @select="selectLinkedinPost"
        @add="createLinkedinPost()"
        @reorder="reorderLinkedinPosts"
      />
    </div>

    <gs-alert-dialog
      v-model="confirmOpen"
      title="Delete this post?"
      :copy="$t('frontend_non_revertable')"
      :confirm-label="$t('frontend_delete_confirm')"
      @confirm="applyAction()"
    />
  </div>
</template>

<script>
import {
  required,
  minLength,
  maxLength,
} from 'vuelidate/lib/validators';
import generateNumberMixin from '@/mixins/generateNumberMixin';
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';
import AssetItemList from './AssetItemList.vue';
import AssetSegmented from './AssetSegmented.vue';
import FieldError from './FieldError.vue';
import { toTabOrder, sortByTabOrder, idFromTabValue } from './tabOrder';

// Soft guidance only: the stored value is never truncated, the counter just
// turns red past the length X shows before the "show more" fold.
const TWEET_LIMIT = 280;

export default {
  components: {
    AssetItemList,
    AssetSegmented,
    FieldError,
    TrashIcon,
  },
  mixins: [generateNumberMixin],
  props: {
    funnelResource: {
      type: Object,
      required: true,
    },
    landingPages: {
      type: Array,
      required: true,
    },
    facebookPostsTab: {
      type: String,
      default: '',
    },
    twitterTweetsTab: {
      type: String,
      default: '',
    },
    linkedinPostsTab: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      itemToDeleteIndex: null,
      actionType: '',
      confirmOpen: false,
      localFunnelResource: this.funnelResource,
      localFacebookPostsTab: this.facebookPostsTab,
      localTwitterTweetsTab: this.twitterTweetsTab,
      localLinkedinPostsTab: this.linkedinPostsTab,
      socialMediaPostsTab: 'facebook',
    }
  },
  computed: {
    /*
     * Every visible network name — the segment, the list title, the add label,
     * the empty copy, the delete labels and the 280-character hint — comes from
     * the one i18n key per network, so renaming a network (Twitter → X) is a
     * single `en.json` edit rather than a hunt through this template. The
     * stored data keys (`facebook` / `twitter` / `linkedin`) never change.
     */
    facebookName() {
      return this.$t('frontend_resources_facebook');
    },
    twitterName() {
      return this.$t('frontend_resources_twitter');
    },
    linkedinName() {
      return this.$t('frontend_resources_linkedin');
    },
    socialMediaPostsTabs() {
      return [
        { title: this.facebookName, value: 'facebook' },
        { title: this.twitterName, value: 'twitter' },
        { title: this.linkedinName, value: 'linkedin' },
      ];
    },
    landingPagesOptions() {
      return this.landingPages.map((landingPage) => {
        return {
          value: landingPage.id,
          text: landingPage.description,
        };
      });
    },
    facebookPosts() {
      return (
        (this.localFunnelResource
          && this.localFunnelResource.social_media_posts
          && this.localFunnelResource.social_media_posts.facebook) || []
      );
    },
    twitterTweets() {
      return (
        (this.localFunnelResource
          && this.localFunnelResource.social_media_posts
          && this.localFunnelResource.social_media_posts.twitter) || []
      );
    },
    linkedinPosts() {
      return (
        (this.localFunnelResource
          && this.localFunnelResource.social_media_posts
          && this.localFunnelResource.social_media_posts.linkedin) || []
      );
    },
    hasFacebookPosts() {
      return (
        this.localFunnelResource &&
        this.localFunnelResource.social_media_posts &&
        this.localFunnelResource.social_media_posts.facebook.length > 0
      );
    },
    hasTwitterPosts() {
      return (
        this.localFunnelResource &&
        this.localFunnelResource.social_media_posts &&
        this.localFunnelResource.social_media_posts.twitter.length > 0
      );
    },
    hasLinkedinPosts() {
      return (
        this.localFunnelResource &&
        this.localFunnelResource.social_media_posts &&
        this.localFunnelResource.social_media_posts.linkedin.length > 0
      );
    },
    selectedFacebookPost() {
      return this.facebookPosts.filter((post) => this.isCurrentFacebookPostsTab(post))[0] || null;
    },
    selectedFacebookPostId() {
      if (!this.localFacebookPostsTab) return null;
      return idFromTabValue('fb_post', this.localFacebookPostsTab);
    },
    selectedTwitterTweet() {
      return this.twitterTweets.filter((post) => this.isCurrentTwitterTweetsTab(post))[0] || null;
    },
    selectedTwitterTweetId() {
      if (!this.localTwitterTweetsTab) return null;
      return idFromTabValue('twitter_tweet', this.localTwitterTweetsTab);
    },
    selectedLinkedinPost() {
      return this.linkedinPosts.filter((post) => this.isCurrentLinkedinPostsTab(post))[0] || null;
    },
    selectedLinkedinPostId() {
      if (!this.localLinkedinPostsTab) return null;
      return idFromTabValue('linkedin_post', this.localLinkedinPostsTab);
    },
    socialMediaPostsErrorTabs() {
      let socialMediaPostsErrorTabs = [];

      if (this.$v.localFunnelResource.social_media_posts.facebook.$error) {
        socialMediaPostsErrorTabs.push('facebook');
      }

      if (this.$v.localFunnelResource.social_media_posts.twitter.$error) {
        socialMediaPostsErrorTabs.push('twitter');
      }

      if (this.$v.localFunnelResource.social_media_posts.linkedin.$error) {
        socialMediaPostsErrorTabs.push('linkedin');
      }

      return socialMediaPostsErrorTabs;
    },
    facebookPostsErrorIds() {
      let facebookPostsErrorIds = [];

      let index;
      for (index in this.$v.localFunnelResource.social_media_posts.facebook.$each.$iter) {
        if (this.$v.localFunnelResource.social_media_posts.facebook.$each.$iter[index].$error) {
          facebookPostsErrorIds.push(
            this.$v.localFunnelResource.social_media_posts.facebook.$each.$iter[index].$model.id
          );
        }
      }

      return facebookPostsErrorIds;
    },
    twitterTweetsErrorIds() {
      let twitterTweetsErrorIds = [];

      let index;
      for (index in this.$v.localFunnelResource.social_media_posts.twitter.$each.$iter) {
        if (this.$v.localFunnelResource.social_media_posts.twitter.$each.$iter[index].$error) {
          twitterTweetsErrorIds.push(
            this.$v.localFunnelResource.social_media_posts.twitter.$each.$iter[index].$model.id
          );
        }
      }

      return twitterTweetsErrorIds;
    },
    linkedinPostsErrorIds() {
      let linkedinPostsErrorIds = [];

      let index;
      for (index in this.$v.localFunnelResource.social_media_posts.linkedin.$each.$iter) {
        if (this.$v.localFunnelResource.social_media_posts.linkedin.$each.$iter[index].$error) {
          linkedinPostsErrorIds.push(
            this.$v.localFunnelResource.social_media_posts.linkedin.$each.$iter[index].$model.id
          );
        }
      }

      return linkedinPostsErrorIds;
    },
  },
  mounted() {
    this.$root.$on('trigger-validation', () => {
      this.$v.$touch();
    });
  },
  methods: {
    characterCount(value) {
      const length = (value || '').length;
      return `${length} ${length === 1 ? 'character' : 'characters'}`;
    },
    tweetCount(value) {
      return `${(value || '').length} / ${TWEET_LIMIT}`;
    },
    isOverTweetLimit(value) {
      return (value || '').length > TWEET_LIMIT;
    },
    affiliateLinksActionsFacebookPost(index) {
      let affiliateLinksActions = [];

      if (this.landingPages.length > 0) {
        for (let i = 0; i < this.landingPages.length; i++) {
          affiliateLinksActions.push({
            text: this.landingPages[i].description,
            callback: () => this.addAffiliateLinkToFacebookPost(index, this.landingPages[i].id),
          });
        }
      }

      return affiliateLinksActions;
    },
    facebookPostInsertActions(index) {
      return [
        {
          text: this.$t('frontend_resources_add_affiliate_name'),
          callback: () => this.addAffiliateNameToFacebookPost(index),
        },
        ...this.affiliateLinksActionsFacebookPost(index),
      ];
    },
    affiliateLinksActionsTwitterTweet(index) {
      let affiliateLinksActions = [];

      if (this.landingPages.length > 0) {
        for (let i = 0; i < this.landingPages.length; i++) {
          affiliateLinksActions.push({
            text: this.landingPages[i].description,
            callback: () => this.addAffiliateLinkToTwitterTweet(index, this.landingPages[i].id),
          });
        }
      }

      return affiliateLinksActions;
    },
    twitterTweetInsertActions(index) {
      return [
        {
          text: this.$t('frontend_resources_add_affiliate_name'),
          callback: () => this.addAffiliateNameToTwitterTweet(index),
        },
        ...this.affiliateLinksActionsTwitterTweet(index),
      ];
    },
    affiliateLinksActionsLinkedinPost(index) {
      let affiliateLinksActions = [];

      if (this.landingPages.length > 0) {
        for (let i = 0; i < this.landingPages.length; i++) {
          affiliateLinksActions.push({
            text: this.landingPages[i].description,
            callback: () => this.addAffiliateLinkToLinkedinPost(index, this.landingPages[i].id),
          });
        }
      }

      return affiliateLinksActions;
    },
    linkedinPostInsertActions(index) {
      return [
        {
          text: this.$t('frontend_resources_add_affiliate_name'),
          callback: () => this.addAffiliateNameToLinkedinPost(index),
        },
        ...this.affiliateLinksActionsLinkedinPost(index),
      ];
    },
    updateFunnelResource() {
      this.$emit('updateFunnelResource', this.localFunnelResource);
    },
    selectFacebookPost(id) {
      this.handleFacebookPostsTabChange(`fb_post_tab_${id}`);
    },
    selectTwitterTweet(id) {
      this.handleTwitterTweetsTabChange(`twitter_tweet_tab_${id}`);
    },
    selectLinkedinPost(id) {
      this.handleLinkedinPostsTabChange(`linkedin_post_tab_${id}`);
    },
    // `AssetItemList` reports the new order as ids; the existing handlers speak
    // the tab shape gd-tabs used, so the ids are wrapped back into it.
    reorderFacebookPosts(ids) {
      this.handleFacebookPostsTabOrderUpdate(toTabOrder('fb_post', ids));
    },
    reorderTwitterTweets(ids) {
      this.handleTwitterTweetsTabOrderUpdate(toTabOrder('twitter_tweet', ids));
    },
    reorderLinkedinPosts(ids) {
      this.handleLinkedinPostsTabOrderUpdate(toTabOrder('linkedin_post', ids));
    },
    handleFacebookPostsTabChange(newTab) {
      this.localFacebookPostsTab = newTab || this.localFacebookPostsTab;
      this.$emit('handleFacebookPostsTabChange', this.localFacebookPostsTab);
    },
    handleTwitterTweetsTabChange(newTab) {
      this.localTwitterTweetsTab = newTab || this.localTwitterTweetsTab;
      this.$emit('handleTwitterTweetsTabChange', this.localTwitterTweetsTab);
    },
    handleLinkedinPostsTabChange(newTab) {
      this.localLinkedinPostsTab = newTab || this.localLinkedinPostsTab;
      this.$emit('handleLinkedinPostsTabChange', this.localLinkedinPostsTab);
    },
    handleSocialMediaPostsTabChange(newTab) {
      this.socialMediaPostsTab = newTab;
    },
    handleFacebookPostsTabOrderUpdate(updatedTabs) {
      sortByTabOrder(
        this.localFunnelResource.social_media_posts.facebook,
        'fb_post',
        updatedTabs
      );

      this.updateFunnelResource();
    },
    createFacebookPost() {
      const newPostId = this.generateNumber();
      const newPostCount = this.localFunnelResource.social_media_posts.facebook.length + 1;

      this.localFunnelResource.social_media_posts.facebook.push({
        id: newPostId,
        name: `Post ${newPostCount}`,
        landingPage: this.landingPages.length ? this.landingPages[0].id : '',
        post: '',
      });
      this.localFacebookPostsTab = `fb_post_tab_${newPostId}`;

      this.updateFunnelResource();
      this.handleFacebookPostsTabChange();
    },
    openConfirmModal(index, actionType) {
      this.itemToDeleteIndex = index;
      this.actionType = actionType;
      this.confirmOpen = true;
    },
    applyAction() {
      switch (this.actionType) {
        case 'deleteFacebookPost':
          this.deleteFacebookPost();
          break;
        case 'deleteTwitterTweet':
          this.deleteTwitterTweet();
          break;
        case 'deleteLinkedinPost':
          this.deleteLinkedinPost();
          break;
      }
    },
    async deleteFacebookPost() {
      this.localFunnelResource.social_media_posts.facebook.splice(this.itemToDeleteIndex, 1);
      this.localFacebookPostsTab = '';

      if (this.localFunnelResource.social_media_posts.facebook.length > 0) {
        let nextOpenIndex = 0;
        if (this.itemToDeleteIndex != 0) {
          nextOpenIndex = this.itemToDeleteIndex - 1;
        }

        const nextOpenId =
          this.localFunnelResource.social_media_posts.facebook[nextOpenIndex].id;
        this.localFacebookPostsTab = `fb_post_tab_${nextOpenId}`;
      }

      this.updateFunnelResource();
      this.handleFacebookPostsTabChange();
    },
    addAffiliateNameToFacebookPost(index) {
      this.localFunnelResource.social_media_posts.facebook[
        index
      ].post = `${this.localFunnelResource.social_media_posts.facebook[index].post}##affiliateName##`;

      this.updateFunnelResource();
    },
    addAffiliateLinkToFacebookPost(index, landingPageId) {
      this.localFunnelResource.social_media_posts.facebook[
        index
      ].post = `${this.localFunnelResource.social_media_posts.facebook[index].post}##affiliateLink${landingPageId}##`;

      this.updateFunnelResource();
    },
    handleTwitterTweetsTabOrderUpdate(updatedTabs) {
      sortByTabOrder(
        this.localFunnelResource.social_media_posts.twitter,
        'twitter_tweet',
        updatedTabs
      );

      this.updateFunnelResource();
    },
    createTwitterTweet() {
      const newPostId = this.generateNumber();
      const newPostCount = this.localFunnelResource.social_media_posts.twitter.length + 1;

      this.localFunnelResource.social_media_posts.twitter.push({
        id: newPostId,
        name: `Tweet ${newPostCount}`,
        landingPage: this.landingPages.length ? this.landingPages[0].id : '',
        tweet: '',
      });
      this.localTwitterTweetsTab = `twitter_tweet_tab_${newPostId}`;

      this.updateFunnelResource();
      this.handleTwitterTweetsTabChange();
    },
    async deleteTwitterTweet() {
      this.localFunnelResource.social_media_posts.twitter.splice(this.itemToDeleteIndex, 1);
      this.localTwitterTweetsTab = '';

      if (this.localFunnelResource.social_media_posts.twitter.length > 0) {
        let nextOpenIndex = 0;
        if (this.itemToDeleteIndex != 0) {
          nextOpenIndex = this.itemToDeleteIndex - 1;
        }

        const nextOpenId = this.localFunnelResource.social_media_posts.twitter[nextOpenIndex].id;
        this.localTwitterTweetsTab = `twitter_tweet_tab_${nextOpenId}`;
      }

      this.updateFunnelResource();
      this.handleTwitterTweetsTabChange();
    },
    addAffiliateNameToTwitterTweet(index) {
      this.localFunnelResource.social_media_posts.twitter[
        index
      ].tweet = `${this.localFunnelResource.social_media_posts.twitter[index].tweet}##affiliateName##`;

      this.updateFunnelResource();
    },
    addAffiliateLinkToTwitterTweet(index, landingPageId) {
      this.localFunnelResource.social_media_posts.twitter[
        index
      ].tweet = `${this.localFunnelResource.social_media_posts.twitter[index].tweet}##affiliateLink${landingPageId}##`;

      this.updateFunnelResource();
    },
    handleLinkedinPostsTabOrderUpdate(updatedTabs) {
      sortByTabOrder(
        this.localFunnelResource.social_media_posts.linkedin,
        'linkedin_post',
        updatedTabs
      );

      this.updateFunnelResource();
    },
    createLinkedinPost() {
      const newPostId = this.generateNumber();
      const newPostCount = this.localFunnelResource.social_media_posts.linkedin.length + 1;

      this.localFunnelResource.social_media_posts.linkedin.push({
        id: newPostId,
        name: `Post ${newPostCount}`,
        landingPage: this.landingPages.length ? this.landingPages[0].id : '',
        post: '',
      });
      this.localLinkedinPostsTab = `linkedin_post_tab_${newPostId}`;

      this.updateFunnelResource();
      this.handleLinkedinPostsTabChange();
    },
    async deleteLinkedinPost() {
      this.localFunnelResource.social_media_posts.linkedin.splice(this.itemToDeleteIndex, 1);
      this.localLinkedinPostsTab = '';

      if (this.localFunnelResource.social_media_posts.linkedin.length > 0) {
        let nextOpenIndex = 0;
        if (this.itemToDeleteIndex != 0) {
          nextOpenIndex = this.itemToDeleteIndex - 1;
        }

        const nextOpenId = this.localFunnelResource.social_media_posts.linkedin[nextOpenIndex].id;
        this.localLinkedinPostsTab = `linkedin_post_tab_${nextOpenId}`;
      }

      this.updateFunnelResource();
      this.handleLinkedinPostsTabChange();
    },
    addAffiliateNameToLinkedinPost(index) {
      this.localFunnelResource.social_media_posts.linkedin[
        index
      ].post = `${this.localFunnelResource.social_media_posts.linkedin[index].post}##affiliateName##`;

      this.updateFunnelResource();
    },
    addAffiliateLinkToLinkedinPost(index, landingPageId) {
      this.localFunnelResource.social_media_posts.linkedin[
        index
      ].post = `${this.localFunnelResource.social_media_posts.linkedin[index].post}##affiliateLink${landingPageId}##`;

      this.updateFunnelResource();
    },
    isCurrentFacebookPostsTab(post) {
      return this.localFacebookPostsTab === 'fb_post_tab_' + post.id;
    },
    isCurrentTwitterTweetsTab(post) {
      return this.localTwitterTweetsTab === 'twitter_tweet_tab_' + post.id;
    },
    isCurrentLinkedinPostsTab(post) {
      return this.localLinkedinPostsTab === 'linkedin_post_tab_' + post.id;
    },
  },
  validations: {
    localFunnelResource: {
      social_media_posts: {
        facebook: {
          $each: {
            name: {
              required,
              minLength: minLength(3),
              maxLength: maxLength(80),
            },
            post: {
              required,
              minLength: minLength(2),
            },
            $trackBy: 'id',
          },
        },
        twitter: {
          $each: {
            name: {
              required,
              minLength: minLength(3),
              maxLength: maxLength(80),
            },
            tweet: {
              required,
              minLength: minLength(2),
            },
            $trackBy: 'id',
          },
        },
        linkedin: {
          $each: {
            name: {
              required,
              minLength: minLength(3),
              maxLength: maxLength(80),
            },
            post: {
              required,
              minLength: minLength(2),
            },
            $trackBy: 'id',
          },
        },
      },
    },
  },
};
</script>

<style src="./asset-split.css"></style>
